import React, { useCallback, useMemo, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Box,
  CardTypesEnum,
  costFormater,
  FlatButton,
  HealthPlanStatuses,
  ServiceConfig,
  Text,
  TextWithPointer,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { ListItemType } from '@/common/types';
import { PHARMACY_TYPE } from '@/modules/HealthPlan/HealthPlanSetup/ComplexConfiguration/EnrollmentMatrix/AddServicePaymentModal/queries/useServiceType.query.ts';
import { useHealthPlanTypes } from '@/modules/HealthPlan/hooks';
import theme from '@/styles/theme';

import ExpandedItem from '../ExpandedItem';
import ExpandedItemFirstLevel from '../ExpandedItem/ExpandedItemFirstLevel';

import ServiceDeleteModal from './ServiceDeleteModal';

export const StyledBox = styled(Box)<{ notClickable?: boolean }>`
  &:hover {
    cursor: ${({ notClickable }) => notClickable && 'not-allowed'};
    background-color: ${theme.colors.accentActive};
    svg {
      fill: ${theme.colors.iconOnColor};
    }
  }
  pointer-events: ${({ notClickable }) => notClickable && 'none'};
`;

interface Props {
  value: ServiceConfig;
  hideOptions?: boolean;
  setUpdatedElement?: (value: ServiceConfig) => void;
  healthPlanStatus?: HealthPlanStatuses;
}
const ServiceConfigItem: React.FC<Props> = ({ value, hideOptions, setUpdatedElement, healthPlanStatus }) => {
  const [isExpanded, setExpanded] = useState(false);
  const [deletedServiceId, setDeleteServiceId] = useState('');
  const healthPlanTypes = useHealthPlanTypes();
  const serviceTypeTitle = useMemo(() => {
    const normalizedServiceType =
      value?.serviceType === PHARMACY_TYPE ? CardTypesEnum.RX : value?.serviceType;
    const found = healthPlanTypes.find((type) => type.key === normalizedServiceType);
    return found ? found.title : value?.serviceType;
  }, [healthPlanTypes, value?.serviceType]);
  const disabledOptionHintText = useMemo(
    () =>
      `${value?.serviceName} is a ${serviceTypeTitle} service. ${serviceTypeTitle} services are no longer configured for this plan. All payment rules associated to this service will be removed when the changes are saved.\n\n To keep this payment rule in this plan, update the health plan type to include ${serviceTypeTitle}.`,
    [serviceTypeTitle, value?.serviceName]
  );
  const handleToggleDeleteModal = useCallback((id: string) => {
    setDeleteServiceId(id);
  }, []);

  const handleToggleExpanded = useCallback(() => {
    setExpanded(!isExpanded);
  }, [isExpanded]);

  return (
    <ExpandedItem
      key={value.id}
      testId={`service_config_item-${value?.id}`}
      isExpanded={isExpanded}
      isDangerMode={value.isDangerMode}
      disabledOptionHint={value.isDangerMode ? disabledOptionHintText : ''}
      options={
        hideOptions
          ? []
          : [
              {
                name: 'Edit',
                onClick: () => {
                  if (setUpdatedElement) setUpdatedElement(value);
                },
              },
              {
                name: 'Delete',
                onClick: () => {
                  handleToggleDeleteModal(value.id);
                },
              },
            ]
      }
      item={{
        id: value.id,
        fields: [
          {
            key: 'org-level-name',
            type: ListItemType.Node,
            title: '',
            flex: 1.5,
            node: (
              <Box data-testid={`service_config_item-${value.serviceId}_org-level-name`}>
                <FlatButton
                  data-testid={`service_config_flat_button-${value.serviceId}_org-level-name`}
                  onClick={handleToggleExpanded}
                >
                  <Box direction="row" align="center">
                    <Box width={{ min: '24px' }} pad={{ right: 's' }}>
                      {isExpanded ? (
                        <ArrowUpIcon color="iconPrimary" />
                      ) : (
                        <ArrowDownIcon color="iconPrimary" />
                      )}
                    </Box>
                    <TextWithPointer
                      title={value.serviceId}
                      size="medium"
                      color="textBody"
                      ellipsisMode
                      weight="bold"
                      style={{ paddingRight: theme.spacings.xs }}
                    >
                      {value.services && value.services.length > 1 ? 'Services' : value.serviceName}
                    </TextWithPointer>
                  </Box>
                </FlatButton>
              </Box>
            ),
          },
          {
            key: 'plan-template',
            type: ListItemType.Text,
            ellipsisMode: true,
            title: value.claimValue ? costFormater(value.claimValue, true) : 'Amount is not specified',
            flex: 1.5,
          },
          {
            key: 'valid-from',
            type: ListItemType.Node,
            node: (
              <Box direction="row" align="center">
                <Box
                  height="10px"
                  width="10px"
                  background={{ color: value.isAppliedDeductible ? 'success' : 'warning' }}
                  round="50%"
                />
                <Text role="status" weight={400} size="medium" margin={{ left: 'xs' }}>
                  {value.isAppliedDeductible ? 'Applied' : 'Not applied'}
                </Text>
              </Box>
            ),
            title: '',
          },
        ],
      }}
    >
      {isExpanded && (
        <ExpandedItemFirstLevel testId={`service_config_item_${value.id}_first-level`} value={value} />
      )}
      <ServiceDeleteModal serviceId={deletedServiceId} setDeleteServiceId={setDeleteServiceId} />
    </ExpandedItem>
  );
};
export default ServiceConfigItem;
